<template>
  <div class="flex-container advanced-table-wrapper">
    <PageTop title="Alt Hesaplar" />
    <div class="main-area">
      <div class="main-area-top-content">
        <Button
          :class="{ 'empty-class': isEmptyTable }"
          primary
          lg
          @click="createNewSubAccount"
        >
          <img src="@/assets/images/icons/plus-white.svg" />
          Yeni Alt Hesap Ekle
        </Button>
        <div class="main-area-top-content-info">
          <span>Mevcut hesap sayısı: {{ data?.length || 0 }}</span>
          <small>Maksimum hesap sayısı: {{ subAccountLimit || 0 }}</small>
        </div>
      </div>
      <div v-if="isLoading" class="loading">
        <img src="@/assets/images/loading.gif" alt="Loading" />
      </div>
      <div v-else-if="isEmptyTable" class="empty">
        <img
          class="empty-img"
          src="@/assets/images/empty-search.png"
          alt="Empty"
        />
        <div class="empty-text">
          <span class="empty-text-title">{{
            "Henüz alt hesabınız bulunmuyor. "
          }}</span>
          <span
            class="empty-text-description"
            v-html="
              'Farklı kullanıcılar için ana hesabınıza bağlı alt hesaplar oluşturabilirsiniz.<br> En fazla 10 adet alt hesap ekleyebilirsiniz.'
            "
          ></span>
        </div>
        <Button primary lg class="w-auto" @click="createNewSubAccount">
          <img src="@/assets/images/icons/plus-white.svg" />
          Yeni Alt Hesap Ekle
        </Button>
      </div>
      <template v-else>
        <div class="table-wrapper">
          <table class="advanced-table">
            <thead>
              <tr
                v-for="headerGroup in table.getHeaderGroups()"
                :key="headerGroup.id"
              >
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :style="{ width: `${header.getSize()}px` }"
                  :colSpan="header.colSpan"
                >
                  <FlexRender
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in table.getRowModel().rows"
                :key="row.id"
                :class="table.options.meta?.getRowClasses(row)"
              >
                <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                  <template v-if="cell.column.columnDef.header === 'İşlem'">
                    <div class="actions-column">
                      <div class="actions-column-delete">
                        <a
                          class="delete-link"
                          target="_blank"
                          @click.prevent="
                            openDeleteModal(cell.row.original.username)
                          "
                        >
                          <span class="icon">
                            <Icons
                              name="Delete"
                              class="delete-icon"
                              size="xs"
                            />
                          </span>
                          Hesabı Sil
                        </a>
                      </div>
                      <span class="separator">|</span>
                      <AppButton
                        style="max-width: fit-content; height: 24px !important"
                        secondary
                        xsm
                        title="Yeni Şifre Gönder"
                        @click="sendNewPassword(cell.row.original.username)"
                      >
                        <template #prependIcon>
                          <Icons
                            name="Return"
                            size="sm"
                            color="#1C1C1E"
                          />
                        </template>
                      </AppButton>
                    </div>
                  </template>
                  <template v-else-if="cell.column.id === 'lastLoginDate'">
                    <span>{{
                      cell.getValue() ? formatTimestamp(cell.getValue()) : "-"
                    }}</span>
                  </template>
                  <template v-else>
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <div>
      <DeleteSubAccountModal
        ref="deleteSubAccountModalRef"
        :selected-username="selectedUsername"
        @successDeleteProduct="handleDataUpdate()"
      />
      <ResetPasswordModal ref="resetPasswordModalRef" />
      <CreateNewSubAccountModal
        ref="createNewSubAccountModalRef"
        @createNewSubAccount="handleDataUpdate()"
        @successModal="openSuccessModal()"
      />
      <CreateSubAccountSuccessModal ref="createSubAccountSuccessModalRef" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { Icons, AppButton, Button } from "@n11/so-ui-components";
import { useVueTable, getCoreRowModel, FlexRender } from "@tanstack/vue-table";
import PageTop from "@/components/Page/PageTop.vue";
import useService from "./services/getSubAccountService";
import { getSubAccountColumns } from "./services/getSubAccountColumns";
import { formatTimestamp } from "@/utils/formatedDate";
import DeleteSubAccountModal from "./partial/DeleteSubAccountModal.vue";
import ResetPasswordModal from "./partial/ResetPasswordModal.vue";
import CreateNewSubAccountModal from "./partial/CreateNewSubAccountModal.vue";
import CreateSubAccountSuccessModal from "./partial/CreateSubAccountSuccessModal.vue";
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const store = useStore();
const isEmptyTable = ref(false);
const tableColumns = ref(getSubAccountColumns());
const selectedUsername = ref(null);

// Accounts module computed properties
const subAccountLimit = computed(
  () => store.getters["accounts/getSubAccountLimit"]
);
// const canCreateNewSubAccount = computed(
//   () => store.getters["accounts/canCreateNewSubAccount"]
// );
// const isCreatingSubAccount = computed(
//   () => store.getters["accounts/isCreatingSubAccount"]
// );
// const isDeletingSubAccount = computed(
//   () => store.getters["accounts/isDeletingSubAccount"]
// );
const { data, isLoading, fetchData } = useService();
const deleteSubAccountModalRef = ref();
const resetPasswordModalRef = ref();
const createNewSubAccountModalRef = ref();
const createSubAccountSuccessModalRef = ref();

const table = ref(
  useVueTable({
    get data() {
      return data?.value ?? [];
    },
    get columns() {
      return tableColumns.value;
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    get rowCount() {
      return data?.value?.length ?? 0;
    },
  })
);

watch(
  () => data.value,
  (newValue) => {
    isLoading.value = false;
    if (newValue) {
      isEmptyTable.value = newValue.length === 0;
    } else {
      isEmptyTable.value = true;
    }
  }
);

const openDeleteModal = (username) => {
  selectedUsername.value = username;
  deleteSubAccountModalRef.value?.openDeleteProductModal();
};

const handleDataUpdate = () => {
  fetchData();
};
const sendNewPassword = async (username) => {
  try {
    const res = await store.dispatch(
      "accounts/resetPasswordSubAccount",
      username
    );
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.SUCCESS,
      description: res.message,
    });

    resetPasswordModalRef.value?.openResetPasswordModal();
  } catch (error) {
    let errorMessage = "";
    if (error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = "Bir hata oluştu";
    }
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      title: ToastMessageTypes.ERROR_TITLE,
      description: errorMessage,
    });
  }
};

const createNewSubAccount = () => {
  createNewSubAccountModalRef.value?.openCreateNewSubAccountModal();
};

const openSuccessModal = () => {
  createSubAccountSuccessModalRef.value?.openSuccessModal();
};

onMounted(() => {
  fetchData();
  // Load sub account limit from accounts module
  store.dispatch("accounts/getSubAccountLimit");
});
</script>
<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.main-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 16px 24px;
  gap: 24px;

  &-top-content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-end;

      span {
        font-size: 16px;
        color: $colorN80;
        font-weight: 700;
      }

      small {
        font-size: 12px;
        color: $colorN60;
      }
    }

    button {
      width: 228px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;
  margin-top: 16px;

  .loading {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $colorD40;
    border-radius: 8px;
    flex-direction: column;
    gap: 24px;

    .empty-img {
      width: 120px;
      height: 120px;
    }

    &-text {
      font-size: 16px;
      color: $colorN80;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-direction: column;
      font-weight: 500;
      font-style: normal;
      line-height: 20px;
      letter-spacing: -0.05px;
      text-align: center;

      &-title {
        font-weight: 700;
      }
    }
  }

  .table-wrapper {
    width: 100%;
    position: relative;
    border: 1px solid $colorD40;
    border-radius: 8px;
    border-spacing: 0;
    min-height: 562px;
    border-collapse: separate;
  }

  .advanced-table {
    width: 100%;
    border-radius: 8px;
    border-collapse: separate;

    thead {
      background: $colorD10;

      tr {
        th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #333;
          font-size: 13px;
          background: $colorD10;
        }

        th:not(:last-child) {
          border-right: 1px solid #d3d6df;
        }

        th:last-child {
          border-top-right-radius: 8px;
        }

        th:first-child {
          border-top-left-radius: 8px;
        }
      }
    }

    tbody {
      tr {
        td {
          padding: 16px 12px;
          text-align: left;
          border-bottom: 2px solid $colorD10;
          font-size: 13px;

          .actions-column {
            display: flex;
            align-items: center;
            gap: 12px;

            &-delete {
              width: 50%;
              display: flex;
              justify-content: center;
            }

            .actions-column-delete {
              cursor: pointer;
            }

            .separator {
              color: $colorN40;
            }

            .delete-link {
              display: flex;
              align-items: center;
              color: $colorM50 !important;
            }
          }
        }

        td:not(:last-child) {
          border-right: 1px solid #d3d6df;
        }

        &.success {
          background-color: $colorS05;
        }
      }
    }
  }

  .delete-icon {
    color: $colorM50 !important;
  }
}

.empty-class {
  visibility: hidden;
}
</style>
